// =============================================================================
// CONFIG READERS — the Supabase-AUTHORITATIVE mode (Lane BV, 13 Sep 2026).
//
// Three kiosk config readers serve Supabase behind a host gate and carry a DRIFT
// GUARD: they fetch the Google Sheet copy as well and serve Supabase only while the
// two agree, falling back to the Sheet on any mismatch.
//   * compliance config  — src/lib/complianceReader.js     (readComplianceConfig)
//   * delivery config    — src/lib/deliveryConfigReader.js (readDeliveryPlatforms / readDeliveryGuidance)
//   * checklist config   — src/lib/checklist.js            (fetchChecklistConfig)
// That guard was right while the Sheet was the boss. The Command Centre editors now
// have "Sheet write OFF" modes (rolled-admin #219 for compliance + delivery config,
// admin #207 door-only mode for checklist config). The moment a Sheet write is
// dropped, the first edit makes Supabase differ from the Sheet, every guarded tablet
// falls back to the STALE Sheet, and the edit never reaches a form — so the
// Sheet-write drop can never be flipped until the readers can trust Supabase alone
// (finding F1 of the admin #219 review).
//
// AUTHORITATIVE mode is that trust, per host, per reader: the reader serves Supabase
// WITHOUT fetching or comparing the Sheet (no Apps Script call at all — that read is
// what the decommission counter must see go to zero). If Supabase fails there is NO
// silent fallback to the Sheet (it would be stale): the reader returns null and the
// screen takes its existing "could not load" path (keep the last cache / keep the
// built-in default / show the same toast a failed Sheet fetch shows today).
//
// SHIPS EMPTY. Each reader owns its EMPTY host set + a ?<thing>auth=on|off per-tab
// override, and calls configAuthoritativeEnabled() below. ONE code-enforced
// precondition (never trust the host list alone): the host must ALREADY be on the
// matching READ gate (COMPLIANCE_CONFIG_READ_HOSTS / DELIVERY_CONFIG_READ_HOSTS /
// CHECKLIST_READ_HOSTS, or their ?…read=on override) — authoritative mode changes HOW
// Supabase is trusted, never WHETHER it is read. A listed/forced host that fails the
// precondition stays in guarded mode and warns ONCE (the stockWriteDropEnabled shape
// in stockSync.js). `off` ALWAYS wins (an instant per-tab kill). With every host set
// empty the OFF path is byte-identical to today: the guarded readers run as before.
//
// Order of flips (each SWITCHON_*.md): read gate → soak → authoritative gate → THEN
// the Command Centre Sheet-write flag. Never the other way round.
// =============================================================================

// Parse the ?<param> override: "on" | "off" | null. Pure (search injectable for
// tests; falls back to window.location.search in the browser; anything else -> null).
export function configAuthoritativeOverride(search, param) {
  try {
    const s = search != null ? search : (typeof window !== "undefined" ? window.location.search : "");
    const q = new URLSearchParams(s).get(param);
    if (q === "on" || q === "off") return q;
  } catch (e) {}
  return null;
}

// The PURE decision — every input explicit, no globals, so the whole truth table
// (host in/out, override on/off, read gate on/off) is unit-tested with no window.
// `off` always wins; a listed/forced host still needs the read gate. Returns true
// only when the reader may stop fetching + comparing the Sheet.
export function decideConfigAuthoritative({ override = null, hostListed = false, readEnabled = false } = {}) {
  if (override === "off") return false;              // instant kill — always wins
  const wanted = override === "on" || hostListed;   // the reviewed go-live switch
  if (!wanted) return false;
  if (!readEnabled) return false;                   // host must already read Supabase
  return true;
}

// Warn-once bookkeeping so a mis-listed host (authoritative set, read gate not on)
// logs a single console line per reader, not one per fetch. Exported for the test.
export const _authoritativeWarned = new Set();

// Runtime combiner the readers call. Reads the host + override + the read-gate
// precondition, and warns ONCE when a listed/forced host can't yet go authoritative.
//   hosts             the reader's EMPTY-by-default host Set
//   param             the ?…auth query-param name
//   readEnabled       the matching read gate's CURRENT value (the precondition)
//   label             console tag, e.g. "compliance-read"
//   readGate          plain-English name of the read gate, for the warning
//   search / hostname injectable for tests (default: window.location)
export function configAuthoritativeEnabled({ hosts, param, readEnabled = false, label = "config", readGate = "the read gate", search, hostname } = {}) {
  let host = hostname != null ? hostname : (typeof window !== "undefined" ? (window.location.hostname || "") : "");
  host = String(host || "").toLowerCase();
  const override = configAuthoritativeOverride(search, param);
  const hostListed = !!(hosts && typeof hosts.has === "function" && hosts.has(host));
  const on = decideConfigAuthoritative({ override, hostListed, readEnabled: !!readEnabled });
  if (!on && (override === "on" || hostListed)) {
    const key = label + "|" + host;
    if (!_authoritativeWarned.has(key)) {
      _authoritativeWarned.add(key);
      try {
        console.warn("[" + label + "] authoritative mode REFUSED (staying in guarded mode, the Sheet is still fetched + compared): read-flip=" + !!readEnabled +
          " — a host must already read Supabase (" + readGate + ") before its reader can stop checking the Sheet. ?" + param + "=off always wins.");
      } catch (e) {}
    }
  }
  return on;
}
